import React, { useState, useEffect } from "react"
import { LayoutPricingCards } from "trimble-terra"
import { formatCommonComponentFields } from "./formatLayoutFunctions"
import formatPageLinks from "../formatPageLinks"
import renderPaddingOrFullBleed from "../renderPaddingOrFullBleed"
import RichTextConnector from "../../../components/RichText/RichTextConnector"
import { getUTMParams } from "../../functions/getUTMParams"
import { Link } from "gatsby-link";
import { useQueryParams, StringParam } from "use-query-params";

const FormatAndRenderLayoutPricingCards = ({
  data,
  locale,
  siteIdentifier,
  homepageSlug,
  currentSpaceId,
  index,
}) => {
  const [utmParams, setUtmParams] = useState({})

  useEffect(() => {
    const params = getUTMParams()
    setUtmParams(params)
  }, [])

  const [anchorTabQuery, _setAnchorTabQuery] = useQueryParams({
    tab: StringParam,
  })

  const componentData = {
    ...data,
    layoutDescription: data?.description,
  }

  const formattedComponentData = formatCommonComponentFields(
    componentData,
    locale,
    siteIdentifier,
    homepageSlug,
    currentSpaceId
  )

  const cardTypeTheme =
    data?.theme === "Black" || data?.theme === "Textured Dark"
      ? "outlined"
      : "normal"

  const formattedPricingCardData = cards => {
    return cards && cards?.length
      ? cards?.map(card => {
          let buttonData = null
          if (card?.buttons && card?.buttons?.length > 0) {
            buttonData = card?.buttons?.map(button => {
              return formatPageLinks(
                button,
                locale,
                button?.url?.__typename,
                homepageSlug,
                currentSpaceId,
                siteIdentifier
              )
            })?.[0]
          }
          if (buttonData) {
            buttonData = {
              label: buttonData?.text,
              destination: buttonData?.url,
            }
          }
          return {
            cardType: "normal",
            header: card?.cardTitle,
            subheader: card?.pricingValue ? card.pricingValue : "",
            priceDescription: card?.priceDescription ? card.priceDescription : "",
            detailsHeader: card?.detailsSectionHeader ? card.detailsSectionHeader : "",
            currency: card?.pricingValue ? "USD" : "",
            description: card?.layoutDescription ? (
              <RichTextConnector
                data={{ content: card?.layoutDescription }}
                overrideTheme={null}
              />
            ) : "",
            details:
              !cards?.details && card?.pricingCardListItems
                ? card?.pricingCardListItems?.map(item => {
                    return {
                      text: item?.value,
                    }
                  })
                : card?.details?.map(item => {
                    return {
                      text: item,
                    }
                  }),
            button: buttonData,
            annual: card?.subscription?.[0] === "Annual" ? true : false,
            monthly: card?.subscription?.[0] === "Monthly" ? true : false,
            notApplicable:
              card?.subscription?.[0] === "N/A"
                ? true
                : card?.subscription?.[0] !== "Annual" &&
                  card?.subscription?.[0] !== "Monthly" &&
                  card?.subscription?.[0] !== "N/A"
                ? true
                : false,
            priceList: {
              USD: {
                currency: card?.pricingValue,
                crossedOutPrice: card?.usdCrossedOutPrice,
              },
              EUR: {
                currency: card?.eurPrice,
                crossedOutPrice: card?.eurCrossedOutPrice,
              },
              GBP: {
                currency: card?.gbpPrice,
                crossedOutPrice: card?.gbpCrossedOutPrice,
              },
              JPY: {
                currency: card?.jpyPrice,
                crossedOutPrice: card?.jpyCrossedOutPrice,
              },
              CAD: {
                currency: card?.cadPrice,
                crossedOutPrice: card?.cadCrossedOutPrice,
              },
              AUD: {
                currency: card?.audPrice,
                crossedOutPrice: card?.audCrossedOutPrice,
              },              
            },
            discount: card?.discount,
            featured: card?.featured,
            featuredText: card?.featureText,
            discountCode: card?.discountCode,
          }
        })
      : []
  }

  const formatTabs = formattedComponentData?.pricingCardsInTabs?.map(tab => {
    return {
      title: tab?.title,
      cards: formattedPricingCardData(tab?.cards),
    }
  })

  const formatCurrencyList = formattedComponentData?.currencyList?.length
    ? formattedComponentData.currencyList.map(currency => {
        return {
          displayName: currency?.value,
          machineName: currency?.key,
        }
      })
    : null

  const updatedComponentData = {
    ...formattedComponentData,
    cards: formatTabs?.length
      ? []
      : formattedPricingCardData(formattedComponentData?.pricingCards),
    cardType: cardTypeTheme,
    subscriptionType: formattedComponentData?.toggleSubscriptionTypes
      ? [
          {
            machineName: "monthly",
            name: formattedComponentData?.toggleSubscriptionOption2,
          },
          {
            machineName: "annual",
            name: formattedComponentData?.toggleSubscriptionOption1,
          },
        ]
      : "",
    toggleOptions: formattedComponentData?.toggleSubscriptionTypes
      ? [
          formattedComponentData?.toggleSubscriptionOption2,
          formattedComponentData?.toggleSubscriptionOption1,
        ]
      : null,
    currencies: formatCurrencyList,
    tabs: formatTabs,
    excludesTaxLabel: formattedComponentData?.excludesTaxText,
    paymentText: formattedComponentData?.paymentFinePrint,
    comparePlansText: formattedComponentData?.comparePlansPricingTextLabel,
    displayCardPaymentOptionsMedia:
      formattedComponentData?.displayCardPaymentOptionsMedia,
  }
  return (
    <LayoutPricingCards
      {...updatedComponentData}
      isWrapped={true}
      utmParams={utmParams}
      parentId={index}
      Link={Link}
      anchorTabQuery={anchorTabQuery?.tab}
    />
  )
}

export default FormatAndRenderLayoutPricingCards
